import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Table, Input, Button, Row, Pagination, Tag, Tooltip, Progress, Badge, Col, Select, DatePicker, message } from 'antd';
import { LeftOutlined, RightOutlined, CloseOutlined, TeamOutlined, CalendarOutlined } from '@ant-design/icons';
import { useProjectContext } from '../../../../core/components/projects/useProjectContext';
import { withRouter, Link } from 'react-router-dom';
import { debounce, startCase } from 'lodash';
import useSearchParams from '../../../../core/hooks/useSearchParams';
import AvatarS3 from '../../../shared/images/AvatarS3';
import { projectPriorityColors, projectStatusColors, projectStatusOptions } from '../configs/project.config';
import '../styles/CustomTable.css';
import '../styles/CustomPagination.css';
import '../styles/Badge.css';
import '../styles/SelectInput.css';
import moment from 'moment';

const { Option } = Select;

const ProjectBoard = ({ history }) => {
  const {
    currentUser,
    fetchProjectsPaginated,
    projects,
    projectBoardPagination,
    setProjectBoardPagination,
    projectsLoading,
    fetchTeamMemberUsers,
    teamMemberUsers,
    teamMemberUsersLoading,
    fetchOwnerUsers,
    ownerUsers,
    ownerUsersLoading,
  } = useProjectContext();

  const [searchParams, setSearchParams] = useSearchParams({
    name: '',
    teamMembers: '',
    owner: '',
    startDate: '',
    endDate: '',
    status: '',
  });
  const [lastSavedTimestamp, setLastSavedTimestamp] = useState(moment().valueOf());

  const debouncedFetchRef = useRef(null);

  useEffect(() => {
    debouncedFetchRef.current = debounce(
      ({ skip, first, sortField = null, sortOrder = 'updatedAt_DESC', name = '', teamMembers = '', owner = '', startDate = '', endDate = '', status = '' }) => {
        const orderBy =
          sortOrder === 'updatedAt_DESC'
            ? 'updatedAt_DESC'
            : sortField
              ? `${sortField}_${sortOrder}`
              : 'updatedAt_DESC';

        const teamMembersArray = teamMembers ? teamMembers.split(',') : [];
        const ownerArray = owner ? owner.split(',') : [];
        const statusArray = status ? status.split(',') : [];

        fetchProjectsPaginated({
          variables: {
            where: {
              AND: [
                { name_contains: name },
                ...(teamMembersArray.length
                  ? [{ team_some: { id_in: teamMembersArray } }]
                  : []),
                ...(ownerArray.length
                  ? [{ owner: { id_in: ownerArray } }]
                  : []),
                ...(startDate && endDate
                  ? [{
                      startDate_gte: startDate,
                      endDate_lte: endDate,
                    }]
                  : []),
                ...(statusArray.length
                  ? [{ status_in: statusArray }]
                  : []),
                {
                  OR: [
                    {
                      owner: {
                        roles_some: {
                          org: {
                            OR: [
                              { id: currentUser.role.org.id },
                              { parentOrg_some: { id: currentUser.role.org.id } },
                            ],
                          },
                        },
                      },
                    },
                  ],
                },
              ],
            },
            orderBy,
            skip,
            first,
          },
        });
      },
      500
    );
  }, [fetchProjectsPaginated, currentUser.role.org.id]);

  const handleFetchProjectsPaginated = useCallback(
    (params) => debouncedFetchRef.current(params),
    []
  );

  const handlePaginationChange = (params) => {
    const updatedPagination = {
      ...projectBoardPagination,
      current: params.page || projectBoardPagination.current,
      pageSize: params.pageSize || projectBoardPagination.pageSize,
    };

    const sortOrder =
      params.sortOrder === 'ascend'
        ? 'ASC'
        : params.sortOrder === 'descend'
          ? 'DESC'
          : 'updatedAt_DESC';

    const sortField =
      sortOrder === 'updatedAt_DESC' ? null : params.sortField || projectBoardPagination.sortField;

    setProjectBoardPagination(updatedPagination);

    handleFetchProjectsPaginated({
      skip: (updatedPagination.current - 1) * updatedPagination.pageSize || 0,
      first: updatedPagination.pageSize,
      sortField,
      sortOrder,
      ...searchParams,
    });
  };

  const handleTableChange = (_pagination, _filters, sorter) => {
    const sortField = sorter.field || null;
    const sortOrder = sorter.order || null;

    handlePaginationChange({
      page: 1,
      sortField,
      sortOrder,
    });
  };

  const handlePaginationSizeChange = (_current, pageSize) => {
    handlePaginationChange({
      page: 1,
      pageSize,
    });
  };

  const itemRender = (_page, type, originalElement) => {
    if (type === 'prev') {
      return (
        <Button icon={<LeftOutlined />} type="text">
          Previous
        </Button>
      );
    }
    if (type === 'next') {
      return (
        <Button type="text">
          Next
          <RightOutlined />
        </Button>
      );
    }
    return originalElement;
  };

  useEffect(() => {
    handleFetchProjectsPaginated({
      skip: (projectBoardPagination.current - 1) * projectBoardPagination.pageSize || 0,
      first: projectBoardPagination.pageSize,
      ...searchParams,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearchChange = (name) => {
    const updatedFilters = { ...searchParams, name };
    setSearchParams(updatedFilters);
    setProjectBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTeamMembersFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      teamMembers: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setProjectBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleOwnerFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      owner: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setProjectBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleStatusFilter = (selectedValues) => {
    const updatedFilters = {
      ...searchParams,
      status: selectedValues.join(','),
    };
    setSearchParams(updatedFilters);
    setProjectBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleTimelineFilter = (dates) => {
    const updatedFilters = {
      ...searchParams,
      startDate: dates && dates.length === 2 ? dates[0].format('YYYY-MM-DD') : '',
      endDate: dates && dates.length === 2 ? dates[1].format('YYYY-MM-DD') : '',
    };
    setSearchParams(updatedFilters);
    setProjectBoardPagination((prev) => ({ ...prev, current: 1 }));
  };

  const hasFilterChanges = useMemo(() => {
    const savedState = localStorage.getItem(`projectState_${currentUser.id}`);
    if (!savedState) {
      return true;
    }

    try {
      const {
        searchParams: savedSearchParams,
        projectBoardPagination: savedPagination,
      } = JSON.parse(savedState);

      const currentState = {
        searchParams,
        projectBoardPagination,
      };

      const savedStateObj = {
        searchParams: savedSearchParams,
        projectBoardPagination: savedPagination,
      };

      return JSON.stringify(currentState) !== JSON.stringify(savedStateObj);
    } catch (error) {
      return true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, projectBoardPagination, currentUser.id, lastSavedTimestamp]);

  const handleSaveFilters = () => {
    if (!hasFilterChanges) {
      message.destroy();
      message.info('No filter changes detected.');
      return;
    }

    const stateToSave = {
      searchParams: { ...searchParams },
      projectBoardPagination,
    };

    localStorage.setItem(`projectState_${currentUser.id}`, JSON.stringify(stateToSave));
    setLastSavedTimestamp(moment().valueOf());
    message.destroy();
    message.success('Filters saved successfully!');
  };

  const handleLoadFilters = () => {
    const savedState = localStorage.getItem(`projectState_${currentUser.id}`);
    if (!savedState) {
      message.destroy();
      message.info('No saved filters found.');
      return;
    }

    try {
      const {
        searchParams: savedSearchParams,
        projectBoardPagination: savedPagination,
      } = JSON.parse(savedState);

      setSearchParams(savedSearchParams);
      setProjectBoardPagination(savedPagination);

      message.destroy();
      message.success('Filters loaded successfully!');
    } catch (error) {
      message.error('Failed to load saved filters.');
    }
  };

  const handleClearFilters = () => {
    const clearedParams = {
      name: '',
      teamMembers: '',
      owner: '',
      startDate: '',
      endDate: '',
      status: '',
    };
    setSearchParams(clearedParams);
    setProjectBoardPagination((prev) => ({
      ...prev,
      current: 1,
    }));
  };

  const handleFetchTeamMemberUsers = useCallback(
    debounce((name = '') => {
      fetchTeamMemberUsers({
        variables: {
          first: 50,
          skip: 0,
          where: {
            AND: [
              { assignedTeamProjects_some: {} },
              {
                roles_some: {
                  roleItems_some: { feature: 'TASK_MANAGEMENT' },
                  org: {
                    OR: [
                      { id: currentUser.role.org.id },
                      { parentOrg_some: { id: currentUser.role.org.id } },
                    ],
                  },
                },
              },
              { name_contains: name },
            ],
          },
        },
      });
    }, 500),
    [currentUser.role.org.id, fetchTeamMemberUsers]
  );

  const handleFetchOwnerUsers = useCallback(
    debounce((name = '') => {
      fetchOwnerUsers({
        variables: {
          first: 50,
          skip: 0,
          where: {
            AND: [
              { createdProjects_some: {} },
              {
                roles_some: {
                  roleItems_some: { feature: 'TASK_MANAGEMENT' },
                  org: {
                    OR: [
                      { id: currentUser.role.org.id },
                      { parentOrg_some: { id: currentUser.role.org.id } },
                    ],
                  },
                },
              },
              { name_contains: name },
            ],
          },
        },
      });
    }, 500),
    [currentUser.role.org.id, fetchOwnerUsers]
  );

  useEffect(() => {
    if (searchParams.teamMembers === '') {
      handleFetchTeamMemberUsers();
    }
  }, [handleFetchTeamMemberUsers, searchParams.teamMembers]);

  useEffect(() => {
    if (searchParams.owner === '') {
      handleFetchOwnerUsers();
    }
  }, [handleFetchOwnerUsers, searchParams.owner]);

  const getProjectProgress = (project) => {
    if (!project.tasks || project.tasks.length === 0) return 0;
    const completedTasks = project.tasks.filter(
      (task) => task.status === 'COMPLETED' || task.status === 'DONE'
    ).length;
    return Math.round((completedTasks / project.tasks.length) * 100);
  };

  const getStatusColor = (status) => {
    const statusColors = {
      NOT_STARTED: 'default',
      IN_PROGRESS: 'processing',
      ON_HOLD: 'warning',
      COMPLETED: 'success',
      CANCELLED: 'error',
      PLANNING: 'processing',
    };
    return statusColors[status] || 'default';
  };

  const columns = [
    {
      title: <span style={{ color: '#64748b' }}>Project Name</span>,
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      render: (text, record) => (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <Tooltip title={record.priority ? `PRIORITY: ${record.priority}` : undefined}>
            <Badge
              dot
              color={projectPriorityColors[record.priority] || 'white'}
              className="custom-badge"
              style={{
                marginRight: 0,
                alignSelf: 'center',
              }}
            />
          </Tooltip>
          <Link
            to={`/tasks/projects/details/${record.id}`}
            style={{
              textDecoration: 'none',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
            }}
          >
            {text}
          </Link>
        </div>
      ),
    },
    {
      title: <span style={{ color: '#64748b' }}>Owner</span>,
      dataIndex: 'owner',
      key: 'owner',
      render: (owner) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {owner ? (
            <>
              <AvatarS3
                imgKey={owner.avatar && owner.avatar.key}
                username={owner.name || '-'}
                avatarProps={{ size: 27 }}
              />
              <span>{owner.name}</span>
            </>
          ) : (
            'N/A'
          )}
        </div>
      ),
    },
    {
      title: <span style={{ color: '#64748b' }}>Team</span>,
      dataIndex: 'team',
      key: 'team',
      render: (team) => {
        if (!team || team.length === 0) return '-';

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <TeamOutlined style={{ fontSize: '16px', color: '#64748b' }} />
            <span>{team.length}</span>
          </div>
        );
      },
    },
    {
      title: <span style={{ color: '#64748b' }}>Timeline</span>,
      dataIndex: 'timeline',
      key: 'timeline',
      render: (_, record) => {
        if (!record.startDate && !record.endDate) return 'N/A';

        const start = record.startDate ? moment(record.startDate).format('MMM D, YYYY') : '-';
        const end = record.endDate ? moment(record.endDate).format('MMM D, YYYY') : '-';

        const today = moment();
        const endDate = record.endDate ? moment(record.endDate) : null;
        const daysLeft = endDate ? endDate.diff(today, 'days') : null;

        let daysLeftText = '';
        let daysLeftColor = '#64748b';

        if (daysLeft !== null) {
          if (daysLeft < 0) {
            daysLeftText = `${Math.abs(daysLeft)} day(s) overdue`;
            daysLeftColor = '#ff4d4f';
          } else if (daysLeft === 0) {
            daysLeftText = 'Due today';
            daysLeftColor = '#faad14';
          } else {
            daysLeftText = `${daysLeft} day(s) left`;
            daysLeftColor = daysLeft <= 7 ? '#faad14' : '#64748b';
          }
        }

        return (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
            <CalendarOutlined style={{ fontSize: '14px', color: '#64748b', marginTop: 2 }} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <div style={{ fontSize: '13px', whiteSpace: 'normal', wordWrap: 'break-word' }}>
                {start} - {end}
              </div>
              {daysLeftText && (
                <div style={{ fontSize: '12px', color: daysLeftColor, fontStyle: 'italic' }}>
                  {daysLeftText}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: <span style={{ color: '#64748b' }}>Progress</span>,
      dataIndex: 'progress',
      key: 'progress',
      render: (_, record) => {
        const progress = getProjectProgress(record);
        return (
          <div style={{ minWidth: 120 }}>
            <Progress percent={progress} size="small" />
          </div>
        );
      },
    },
    {
      title: <span style={{ color: '#64748b' }}>Status</span>,
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (status) => {
        if (!status) return 'N/A';
        const formattedStatus = status
          .split('_')
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        return <Tag color={getStatusColor(status)}>{formattedStatus}</Tag>;
      },
    },
  ];

  return (
    <div style={{ backgroundColor: 'white', minHeight: '100vh' }}>
      <Row justify="end" style={{ marginBottom: 16, gap: '10px' }}>
        <Input
          placeholder="Search projects"
          value={searchParams.name || ''}
          onChange={(e) => {
            handleSearchChange(e.target.value);
          }}
          style={{ width: 250, height: '40px', borderRadius: '5px' }}
          prefix={
            <Button
              type="text"
              icon={<CloseOutlined style={{ fontSize: '13px' }} />}
              onClick={() =>
                setSearchParams({
                  ...searchParams,
                  name: '',
                })
              }
            />
          }
        />
        <Button
          onClick={() => {
            history.push(`/tasks/projects/new`);
          }}
          style={{
            fontSize: '15px',
            height: '41px',
            borderRadius: '5px',
            backgroundColor: '#3b82f6',
            color: 'white',
          }}
        >
          Create Project
        </Button>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }} wrap>
        <Col xs={24} sm={12} md={6} lg={4} xl={4}>
          <div style={{ marginBottom: 8, fontSize: '15px' }}>Team Members</div>
          <Select
            className="custom-select"
            mode="multiple"
            showSearch
            style={{ width: '100%' }}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            filterSort={(optionA, optionB) =>
              optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
            }
            onSearch={(searchText) => handleFetchTeamMemberUsers(searchText)}
            loading={teamMemberUsersLoading}
            onChange={handleTeamMembersFilter}
            value={searchParams.teamMembers ? searchParams.teamMembers.split(',') : []}
            maxTagCount={1}
            placeholder="Filter by Team Member"
            allowClear
          >
            {teamMemberUsers.map((user) => (
              <Option key={user.id} value={user.id}>
                {startCase(user.name || user.username)}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={6} lg={4} xl={4}>
          <div style={{ marginBottom: 8, fontSize: '15px' }}>Owner</div>
          <Select
            className="custom-select"
            mode="multiple"
            showSearch
            style={{ width: '100%' }}
            filterOption={(input, option) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            filterSort={(optionA, optionB) =>
              optionA.children.toLowerCase().localeCompare(optionB.children.toLowerCase())
            }
            onSearch={(searchText) => handleFetchOwnerUsers(searchText)}
            loading={ownerUsersLoading}
            onChange={handleOwnerFilter}
            value={searchParams.owner ? searchParams.owner.split(',') : []}
            maxTagCount={1}
            placeholder="Filter by Owner"
            allowClear
          >
            {ownerUsers.map((user) => (
              <Option key={user.id} value={user.id}>
                {startCase(user.name || user.username)}
              </Option>
            ))}
          </Select>
        </Col>

        <Col xs={24} sm={12} md={6} lg={4} xl={4}>
          <div style={{ marginBottom: 8, fontSize: '15px' }}>Timeline</div>
          <DatePicker.RangePicker
            className="custom-date"
            style={{
              borderRadius: '5px',
              display: 'flex',
              alignItems: 'center',
              minHeight: '40px',
              color: 'black',
            }}
            value={
              searchParams.startDate && searchParams.endDate
                ? [
                    moment(searchParams.startDate, 'YYYY-MM-DD'),
                    moment(searchParams.endDate, 'YYYY-MM-DD'),
                  ]
                : []
            }
            onChange={handleTimelineFilter}
            placeholder={['Start Date', 'End Date']}
            allowClear
            format="YYYY-MM-DD"
          />
        </Col>

        <Col xs={24} sm={12} md={6} lg={4} xl={4}>
          <div style={{ marginBottom: 8, fontSize: '15px' }}>Status</div>
          <Select
            mode="multiple"
            className="custom-select"
            style={{ width: '100%' }}
            onChange={handleStatusFilter}
            value={searchParams.status ? searchParams.status.split(',') : []}
            maxTagCount={1}
            placeholder="Filter by Status"
            allowClear
          >
            {projectStatusOptions.map((status) => (
              <Option key={status.value} value={status.value}>
                {status.label}
              </Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Row style={{ marginBottom: 16, gap: '8px' }} justify="end">
        <Button
          style={{
            fontSize: '15px',
            height: '35px',
            borderRadius: '5px',
          }}
          disabled={
            !searchParams ||
            !Object.values(searchParams).some((param) => param && param !== '')
          }
          onClick={handleClearFilters}
          icon={<CloseOutlined style={{ fontSize: '13px' }} />}
        >
          Clear Filters
        </Button>
        <Button
          onClick={handleLoadFilters}
          style={{
            fontSize: '15px',
            height: '35px',
            borderRadius: '5px',
          }}
        >
          Load Filters
        </Button>
        <Button
          onClick={handleSaveFilters}
          type="primary"
          style={{
            fontSize: '15px',
            height: '35px',
            borderRadius: '5px',
            backgroundColor: '#3b82f6',
            color: 'white',
          }}
        >
          Save Filters
        </Button>
      </Row>

      <Table
        columns={columns}
        dataSource={projects}
        loading={projectsLoading}
        pagination={false}
        scroll={{ x: 1000 }}
        className="custom-table"
        onChange={handleTableChange}
      />
      <Pagination
        current={projectBoardPagination.current}
        pageSize={projectBoardPagination.pageSize}
        total={projectBoardPagination.total}
        showSizeChanger
        hideOnSinglePage={false}
        onChange={(page, pageSize) => handlePaginationChange({ page, pageSize })}
        onShowSizeChange={handlePaginationSizeChange}
        style={{ marginTop: 16, paddingBottom: 16 }}
        itemRender={itemRender}
        className="custom-pagination"
      />
    </div>
  );
};

export default withRouter(ProjectBoard);
